/*#
# Seeded data

"Speed is the feature" has to be measured, not hoped for. `seedEvents()`
generates a deterministic event log — thousands of tasks, tens of thousands
of events, the tag vocabulary a real ecosystem would carry — so the board can
be exercised at scale from the first commit. Same seed, same log, every run.
*/

/*{ "parent": "Reference", "order": 80 }*/

import type { EventDraft } from './model'

export interface SeedOptions {
  /** Number of tasks. Default 500. */
  tasks?: number
  /** PRNG seed. Default 1. */
  seed?: number
  /** ISO start time. Events step forward from here. */
  start?: string
  /** Average extra events per task after creation. Default 6. */
  churn?: number
  /**
   * Enroll every seeded project (board #1121), so a throwaway board
   * behaves like one its projects adopted: the brief speaks for them.
   * Appended after the tasks; the random sequence is unchanged.
   */
  enrolled?: boolean
}

const OWNERS = ['owner', 'agent:tosijs', 'agent:tosijs-ui', 'agent:virta']
const SIZES = ['1', '2', '3', '5', '8', '13']
const PROJECTS = [
  'tosijs',
  'tosijs-ui',
  'tosijs-schema',
  'tjs-lang',
  'tosijs-virta',
  'service-compris',
  'haltija',
]
const KINDS = ['bug', 'feature', 'chore', 'docs', 'question']
const AREAS = [
  'ui/board',
  'ui/card',
  'store',
  'sync',
  'auth',
  'adapter/github',
  'docs',
  'release',
]
const PRIORITIES = ['critical', 'high', 'medium', 'medium', 'low', 'low']
const STATUSES = [
  'backlog',
  'ready',
  'in-progress',
  'review',
  'done',
  'freezer',
  'obsolete',
]
const PEOPLE = [
  'owner',
  'agent:tosijs',
  'agent:tosijs-ui',
  'agent:virta',
  'github:anssip',
  'github:reporter',
]
const WORDS =
  'observer binding path proxy slot component board card tag filter event log cursor sync token identity adapter issue release gate test lane fold transcript salience inbox lane keyboard virtual list schema validate manifest install configurator snapshot blob attachment subscription notify'.split(
    ' '
  )

/** mulberry32 — small, fast, good enough for fixtures. */
export function prng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function seedEvents(options: SeedOptions = {}): EventDraft[] {
  const {
    tasks = 500,
    seed = 1,
    start = '2026-01-05T09:00:00.000Z',
    churn = 6,
  } = options
  const rand = prng(seed)
  const pick = <T>(xs: readonly T[]): T => xs[Math.floor(rand() * xs.length)]!
  const words = (n: number) =>
    Array.from({ length: n }, () => pick(WORDS)).join(' ')
  let t = Date.parse(start)
  const tick = () => {
    t += 30_000 + Math.floor(rand() * 4 * 3_600_000)
    return new Date(t).toISOString()
  }
  let n = 0
  const id = (prefix: string) => `${prefix}${(++n).toString(36)}`
  const drafts: EventDraft[] = []

  for (let i = 0; i < tasks; i++) {
    const taskId = id('t')
    const by = pick(PEOPLE)
    const project = pick(PROJECTS)
    const tags = [`kind:${pick(KINDS)}`]
    // a few land with no project: the tool calls that out rather than requiring one
    if (rand() >= 0.04) tags.unshift(`project:${project}`)
    if (rand() < 0.7) tags.push(`area:${pick(AREAS)}`)
    if (rand() < 0.75) tags.push(`status:${pick(STATUSES)}`)
    if (rand() < 0.45) tags.push(`priority:${pick(PRIORITIES)}`)
    if (rand() < 0.15) tags.push(`size:${pick(SIZES)}`)
    // owner: who is responsible — a person or a project's agent; not everything has one
    if (rand() < 0.4) tags.push(`owner:${pick(OWNERS)}`)
    if (rand() < 0.06) tags.push('blocked')
    if (by.startsWith('github:')) tags.push('source:github', 'authority:github')
    if (rand() < 0.1) tags.push('upstream')
    drafts.push({
      id: id('e'),
      taskId,
      at: tick(),
      by,
      kind: 'created',
      payload: {
        title: `${words(2 + Math.floor(rand() * 5))}`,
        body: rand() < 0.6 ? `${words(12 + Math.floor(rand() * 30))}.` : '',
        tags,
      },
    })
    // Track what a real writer would see, so churn is shaped like real moves:
    // a status change untags the old value first; closes only close open tasks.
    let status: string | null =
      tags.find((t) => t.startsWith('status:'))?.slice(7) ?? null
    let closed = false
    const extra = Math.floor(rand() * churn * 2)
    for (let j = 0; j < extra; j++) {
      const r = rand()
      const who = pick(PEOPLE)
      const base = () => ({ id: id('e'), taskId, at: tick(), by: who })
      if (r < 0.35) {
        drafts.push({
          ...base(),
          kind: 'commented',
          payload: { text: `${words(4 + Math.floor(rand() * 14))}.` },
        })
      } else if (r < 0.6) {
        const next = pick(STATUSES)
        if (next === status) continue
        if (status !== null)
          drafts.push({
            ...base(),
            kind: 'untagged',
            payload: { tag: `status:${status}` },
          })
        drafts.push({
          ...base(),
          kind: 'tagged',
          payload: { tag: `status:${next}` },
        })
        status = next
      } else if (r < 0.7) {
        if (status === null) continue
        drafts.push({
          ...base(),
          kind: 'untagged',
          payload: { tag: `status:${status}` },
        })
        status = null
      } else if (r < 0.8) {
        drafts.push({
          ...base(),
          kind: 'edited',
          payload: { body: `${words(10 + Math.floor(rand() * 20))}.` },
        })
      } else if (r < 0.88) {
        drafts.push({
          ...base(),
          kind: 'subscribed',
          payload: { subscriber: pick(PROJECTS) },
        })
      } else if (r < 0.93) {
        drafts.push({
          ...base(),
          kind: 'attached',
          payload: { name: `${pick(WORDS)}.png`, url: `blob:${id('b')}` },
        })
      } else if (!closed) {
        drafts.push({
          ...base(),
          kind: 'closed',
          payload: { reason: rand() < 0.5 ? 'fixed' : '' },
        })
        closed = true
      } else {
        drafts.push({ ...base(), kind: 'reopened', payload: {} })
        closed = false
      }
    }
  }
  if (options.enrolled)
    for (const name of PROJECTS)
      drafts.push({
        id: `seed:enroll:${name}`,
        taskId: `project:${name}`,
        at: new Date(t).toISOString(),
        by: 'owner',
        kind: 'project',
        payload: {
          name,
          settings: { enrolled: `${start.slice(0, 10)} by owner` },
        },
      })
  return drafts
}
